import React, { useState, useEffect } from 'react';
import {
  Settings,
  User,
  Globe,
  DollarSign,
  Calendar,
  Clock,
  Bell,
  Shield,
  Save,
  ChevronRight,
  Mail,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import PasswordInput from '../components/PasswordInput';
import NotificationSettings from '../components/NotificationSettings';
import NotificationHistory from '../components/NotificationHistory';
import TwoFactorSettings from '../components/TwoFactorSettings';
import CompanyCustomizationManager from '../components/company-customization/CompanyCustomizationManager';
import type { I18nConfig, SupportedLanguage } from '../i18n/config';
import { languages } from '../i18n/config';
import { localePresets } from '../i18n/localePresets';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';

const Configuracoes: React.FC = () => {
  const { config: i18nConfig, updateConfig: updateI18nConfig, t } = useI18n();
  const { user } = useAuth();
  const { hasFeature } = useCompanyFeatures();
  const [activeSection, setActiveSection] = useState<
    | 'account'
    | 'notifications'
    | 'push-notifications'
    | 'notification-history'
    | 'security'
    | 'smtp'
    | 'whatsapp'
    | 'company-customization'
  >('account');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const canUseNotificationHistory = hasFeature('notifications.history');

  // Account Settings (sync with i18n context)
  const [accountSettings, setAccountSettings] = useState<I18nConfig>(i18nConfig);

  useEffect(() => {
    setAccountSettings(i18nConfig);
  }, [i18nConfig]);

  // Carregar configuração SMTP quando a seção for aberta
  useEffect(() => {
    if (activeSection === 'smtp') {
      loadSMTPConfig();
    }
  }, [activeSection]);

  // Carregar configuração WhatsApp quando a seção for aberta
  useEffect(() => {
    if (activeSection === 'whatsapp') {
      loadWhatsAppConfig();
    }
  }, [activeSection]);

  const loadSMTPConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/smtp/config', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const config = await response.json();
        setSmtpConfig({
          host: config.host || '',
          port: config.port || 587,
          username: config.username || '',
          password: '', // Não carregar senha
          from: config.from || '',
          secure: config.secure !== false
        });
      } else if (response.status === 404) {
        // Nenhuma configuração encontrada, usar valores padrão
        setSmtpConfig({
          host: '',
          port: 587,
          username: '',
          password: '',
          from: '',
          secure: true
        });
      } else {
        // Em caso de erro, usar valores padrão
        setSmtpConfig({
          host: '',
          port: 587,
          username: '',
          password: '',
          from: '',
          secure: true
        });
      }
    } catch (error) {
      console.error('Error loading SMTP config:', error);
      // Em caso de erro, usar valores padrão
      setSmtpConfig({
        host: '',
        port: 587,
        username: '',
        password: '',
        from: '',
        secure: true
      });
    }
  };

  const saveSMTPConfig = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/smtp/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(smtpConfig)
      });

      if (response.ok) {
        setIsLoading(false);
        setSuccessMessage('Configuração SMTP salva com sucesso');
        setTimeout(() => setSuccessMessage(''), 5000);
        // Limpar senha após salvar
        setSmtpConfig({ ...smtpConfig, password: '' });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        setErrorMessage(`Erro ao salvar: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Erro ao salvar configuração SMTP');
      console.error('Error saving SMTP config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const testSMTPConfig = async () => {
    if (!testEmail) {
      setErrorMessage('Por favor, informe um email para teste');
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      // Garantir que port seja número e secure seja boolean
      const testData = {
        host: smtpConfig.host,
        port: typeof smtpConfig.port === 'string' ? parseInt(smtpConfig.port) || 587 : smtpConfig.port,
        username: smtpConfig.username,
        password: smtpConfig.password,
        from: smtpConfig.from,
        secure: typeof smtpConfig.secure === 'string' ? smtpConfig.secure === 'true' : Boolean(smtpConfig.secure),
        test_to: testEmail
      };

      const response = await fetch('/api/v1/smtp/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testData)
      });

      if (response.ok) {
        setIsLoading(false);
        setSuccessMessage('Email de teste enviado com sucesso! Verifique sua caixa de entrada.');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const errorData = await response.json();
        setErrorMessage(`Erro ao testar: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Erro ao testar configuração SMTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderNotifications: true,
    marketingEmails: false
  });

  // Security Settings
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // SMTP Settings
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from: '',
    secure: true
  });
  const [testEmail, setTestEmail] = useState('');

  // WhatsApp Settings
  const [whatsappConfig, setWhatsAppConfig] = useState({
    account_sid: '',
    auth_token: '',
    from_number: ''
  });
  const [testWhatsAppNumber, setTestWhatsAppNumber] = useState('');

  const loadWhatsAppConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/whatsapp/config', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Verificar se a resposta é JSON antes de fazer parse
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const config = await response.json();
          setWhatsAppConfig({
            account_sid: config.account_sid || '',
            auth_token: '', // Não carregar token
            from_number: config.from_number || ''
          });
        } else {
          // Resposta não é JSON, provavelmente HTML (404 ou erro)
          console.warn('WhatsApp config endpoint returned non-JSON response');
          setWhatsAppConfig({
            account_sid: '',
            auth_token: '',
            from_number: ''
          });
        }
      } else {
        // Nenhuma configuração encontrada, usar valores padrão
        setWhatsAppConfig({
          account_sid: '',
          auth_token: '',
          from_number: ''
        });
      }
    } catch (error) {
      // Tratar erros de parse JSON separadamente
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.warn('WhatsApp config endpoint returned invalid JSON (likely HTML error page)');
      } else {
        console.error('Error loading WhatsApp config:', error);
      }
      setWhatsAppConfig({
        account_sid: '',
        auth_token: '',
        from_number: ''
      });
    }
  };

  const saveWhatsAppConfig = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/whatsapp/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_sid: whatsappConfig.account_sid,
          auth_token: whatsappConfig.auth_token,
          from_number: whatsappConfig.from_number
        })
      });

      if (response.ok) {
        setIsLoading(false);
        setSuccessMessage('Configuração WhatsApp salva com sucesso');
        setTimeout(() => setSuccessMessage(''), 5000);
        // Limpar token após salvar
        setWhatsAppConfig({ ...whatsappConfig, auth_token: '' });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        setErrorMessage(`Erro ao salvar: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Erro ao salvar configuração WhatsApp');
      console.error('Error saving WhatsApp config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const testWhatsAppConfig = async () => {
    if (!testWhatsAppNumber) {
      setErrorMessage('Por favor, informe um número de telefone para teste');
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const testData = {
        account_sid: whatsappConfig.account_sid,
        auth_token: whatsappConfig.auth_token,
        from_number: whatsappConfig.from_number,
        test_number: testWhatsAppNumber
      };

      const response = await fetch('/api/v1/whatsapp/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testData)
      });

      if (response.ok) {
        setIsLoading(false);
        setSuccessMessage('Mensagem de teste enviada com sucesso! Verifique seu WhatsApp.');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const errorData = await response.json();
        setErrorMessage(`Erro ao testar: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Erro ao testar configuração WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  const saveAccountSettings = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Update i18n context
      updateI18nConfig(accountSettings);

      // Apply changes immediately
      document.documentElement.lang = accountSettings.language;

      // Atualizar configurações I18n se o idioma mudou
      if (accountSettings.language !== i18nConfig.language) {
        updateI18nConfig({
          language: accountSettings.language,
          currency: accountSettings.currency,
          dateFormat: accountSettings.dateFormat,
          timeFormat: accountSettings.timeFormat,
          timezone: accountSettings.timezone,
          numberFormat: accountSettings.numberFormat
        });
      }

      // TODO: Implementar chamada à API para salvar no backend
      setTimeout(() => {
        setIsLoading(false);
        setSuccessMessage(t('settingsSaved'));
        setTimeout(() => setSuccessMessage(''), 5000);
      }, 1000);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Erro ao salvar configurações');
    }
  };

  const saveNotificationSettings = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // TODO: Implementar chamada à API
      setTimeout(() => {
        setIsLoading(false);
        setSuccessMessage(t('settingsSaved'));
        setTimeout(() => setSuccessMessage(''), 3000);
      }, 1000);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(t('error'));
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage(t('passwordsDoNotMatch'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setErrorMessage(t('passwordTooShort'));
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/users/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (response.ok) {
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSuccessMessage(t('settingsSaved'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setErrorMessage(`${t('error')}: ${errorData.error || t('error')}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessage(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('settingsTitle')}</h1>
        <p style={{ color: '#666', margin: 0 }}>{t('settingsSubtitle')}</p>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '6px',
          color: '#065f46',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ⚠️ {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Sidebar */}
        <div style={{
          width: '250px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '16px'
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t('settingsMenu')}
          </h3>

          <button
            data-testid="config-section-account"
            onClick={() => setActiveSection('account')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'account' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'account' ? '3px solid #3b82f6' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
              color: activeSection === 'account' ? '#3b82f6' : '#4b5563',
              fontWeight: activeSection === 'account' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <User size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('myAccount')}</span>
            <ChevronRight size={16} />
          </button>

          <button
            data-testid="config-section-notifications"
            onClick={() => setActiveSection('notifications')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'notifications' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'notifications' ? '3px solid #3b82f6' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
              color: activeSection === 'notifications' ? '#3b82f6' : '#4b5563',
              fontWeight: activeSection === 'notifications' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <Bell size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('notifications')}</span>
            <ChevronRight size={16} />
          </button>

          <button
            data-testid="config-section-push-notifications"
            onClick={() => setActiveSection('push-notifications')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'push-notifications' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'push-notifications' ? '3px solid #3b82f6' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
              color: activeSection === 'push-notifications' ? '#3b82f6' : '#4b5563',
              fontWeight: activeSection === 'push-notifications' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <Bell size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>Push Notifications</span>
            <ChevronRight size={16} />
          </button>

          {canUseNotificationHistory && (
            <button
              data-testid="config-section-notification-history"
              onClick={() => setActiveSection('notification-history')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: activeSection === 'notification-history' ? '#eff6ff' : 'transparent',
                border: 'none',
                borderLeft:
                  activeSection === 'notification-history' ? '3px solid #3b82f6' : '3px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '8px',
                fontSize: '14px',
                color: activeSection === 'notification-history' ? '#3b82f6' : '#4b5563',
                fontWeight: activeSection === 'notification-history' ? '600' : '400',
                transition: 'all 0.2s',
              }}
            >
              <Bell size={18} />
              <span style={{ flex: 1, textAlign: 'left' }}>Histórico</span>
              <ChevronRight size={16} />
            </button>
          )}

          <button
            data-testid="config-section-smtp"
            onClick={() => setActiveSection('smtp')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'smtp' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'smtp' ? '3px solid #3b82f6' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
              color: activeSection === 'smtp' ? '#3b82f6' : '#4b5563',
              fontWeight: activeSection === 'smtp' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <Mail size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('smtp.settingsTitle')}</span>
            <ChevronRight size={16} />
          </button>

          <button
            data-testid="config-section-whatsapp"
            onClick={() => setActiveSection('whatsapp')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'whatsapp' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'whatsapp' ? '3px solid #25D366' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '8px',
              fontSize: '14px',
              color: activeSection === 'whatsapp' ? '#25D366' : '#4b5563',
              fontWeight: activeSection === 'whatsapp' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <MessageSquare size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('settings.whatsapp')}</span>
            <ChevronRight size={16} />
          </button>

          {user?.role === 'admin' && (
            <button
              data-testid="config-section-company-customization"
              onClick={() => setActiveSection('company-customization')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: activeSection === 'company-customization' ? '#eff6ff' : 'transparent',
                border: 'none',
                borderLeft:
                  activeSection === 'company-customization'
                    ? '3px solid #4f46e5'
                    : '3px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                marginBottom: '8px',
                fontSize: '14px',
                color: activeSection === 'company-customization' ? '#4f46e5' : '#4b5563',
                fontWeight: activeSection === 'company-customization' ? '600' : '400',
                transition: 'all 0.2s',
              }}
            >
              <Sparkles size={18} />
              <span style={{ flex: 1, textAlign: 'left' }}>{t('settings.customizationByCompany')}</span>
              <ChevronRight size={16} />
            </button>
          )}

          <button
            data-testid="config-section-security"
            onClick={() => setActiveSection('security')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: activeSection === 'security' ? '#eff6ff' : 'transparent',
              border: 'none',
              borderLeft: activeSection === 'security' ? '3px solid #3b82f6' : '3px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              color: activeSection === 'security' ? '#3b82f6' : '#4b5563',
              fontWeight: activeSection === 'security' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            <Shield size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('security')}</span>
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1 }}>
          {/* My Account Section */}
          {activeSection === 'account' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '24px'
            }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>{t('myAccount')}</h2>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                  {t('regionalSettings')}
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Language */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <Globe size={16} />
                      {t('language')}
                    </label>
                    <select
                      value={accountSettings.language}
                      onChange={(e) => {
                        const language = e.target.value as SupportedLanguage;
                        const preset = localePresets[language];
                        setAccountSettings({
                          ...accountSettings,
                          language,
                          ...preset,
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {(Object.keys(languages) as SupportedLanguage[]).map((code) => (
                        <option key={code} value={code}>
                          {languages[code].nativeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Currency */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <DollarSign size={16} />
                      {t('currency')}
                    </label>
                    <select
                      value={accountSettings.currency}
                      onChange={(e) => setAccountSettings({ ...accountSettings, currency: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="USD">USD - US Dollar ($)</option>
                      <option value="BRL">BRL - Brazilian Real (R$)</option>
                      <option value="EUR">EUR - Euro (€)</option>
                      <option value="GBP">GBP - British Pound (£)</option>
                      <option value="JPY">JPY - Japanese Yen (¥)</option>
                    </select>
                  </div>

                  {/* Date Format */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <Calendar size={16} />
                      {t('dateFormat')}
                    </label>
                    <select
                      value={accountSettings.dateFormat}
                      onChange={(e) => setAccountSettings({ ...accountSettings, dateFormat: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY (EU/BR)</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
                      <option value="DD.MM.YYYY">DD.MM.YYYY (DE)</option>
                    </select>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                      Preview: {(() => {
                        const date = new Date();
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear();

                        switch (accountSettings.dateFormat) {
                          case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
                          case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
                          case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
                          case 'DD.MM.YYYY': return `${day}.${month}.${year}`;
                          default: return date.toLocaleDateString();
                        }
                      })()}
                    </p>
                  </div>

                  {/* Time Format */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <Clock size={16} />
                      {t('timeFormat')}
                    </label>
                    <select
                      value={accountSettings.timeFormat}
                      onChange={(e) =>
                        setAccountSettings({
                          ...accountSettings,
                          timeFormat: e.target.value as I18nConfig['timeFormat'],
                        })
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="12h">12-hour (3:30 PM)</option>
                      <option value="24h">24-hour (15:30)</option>
                    </select>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                      Preview: {(() => {
                        const date = new Date();
                        const hour = date.getHours();
                        const minute = date.getMinutes().toString().padStart(2, '0');

                        if (accountSettings.timeFormat === '12h') {
                          const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          return `${hour12}:${minute} ${ampm}`;
                        } else {
                          return `${hour.toString().padStart(2, '0')}:${minute}`;
                        }
                      })()}
                    </p>
                  </div>

                  {/* Timezone */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <Globe size={16} />
                      {t('timezone')}
                    </label>
                    <select
                      value={accountSettings.timezone}
                      onChange={(e) => setAccountSettings({ ...accountSettings, timezone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="America/Sao_Paulo">Brasília Time (BRT)</option>
                      <option value="Europe/London">London (GMT)</option>
                      <option value="Europe/Paris">Paris (CET)</option>
                      <option value="Asia/Tokyo">Tokyo (JST)</option>
                    </select>
                  </div>

                  {/* Number Format */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <Settings size={16} />
                      {t('numberFormat')}
                    </label>
                    <select
                      value={accountSettings.numberFormat}
                      onChange={(e) => setAccountSettings({ ...accountSettings, numberFormat: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="en-US">1,234.56 (US)</option>
                      <option value="pt-BR">1.234,56 (BR)</option>
                      <option value="de-DE">1.234,56 (DE)</option>
                      <option value="fr-FR">1 234,56 (FR)</option>
                    </select>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                      Preview: {(1234.56).toLocaleString(accountSettings.numberFormat)}
                    </p>
                  </div>
                </div>

                {/* Preview das configurações atuais */}
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px',
                  border: '1px solid #bae6fd'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#0369a1' }}>{t('settings.currentApplied')}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                    <div>
                      <strong>Idioma:</strong> {accountSettings.language.toUpperCase()}
                    </div>
                    <div>
                      <strong>Moeda:</strong> {accountSettings.currency}
                    </div>
                    <div>
                      <strong>Data:</strong> {(() => {
                        const date = new Date();
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear();

                        switch (accountSettings.dateFormat) {
                          case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
                          case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
                          case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
                          case 'DD.MM.YYYY': return `${day}.${month}.${year}`;
                          default: return date.toLocaleDateString();
                        }
                      })()}
                    </div>
                    <div>
                      <strong>Hora:</strong> {(() => {
                        const date = new Date();
                        const hour = date.getHours();
                        const minute = date.getMinutes().toString().padStart(2, '0');

                        if (accountSettings.timeFormat === '12h') {
                          const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          return `${hour12}:${minute} ${ampm}`;
                        } else {
                          return `${hour.toString().padStart(2, '0')}:${minute}`;
                        }
                      })()}
                    </div>
                  </div>
                </div>

                <button
                  onClick={saveAccountSettings}
                  disabled={isLoading}
                  style={{
                    marginTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  <Save size={16} />
                  {isLoading ? t('saving') : t('saveSettings')}
                </button>
              </div>

              {/* User Info */}
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                  Account Information
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Email:</span>
                    <span style={{ fontWeight: '500' }}>{user?.email}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Role:</span>
                    <span style={{
                      fontWeight: '500',
                      padding: '2px 8px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {user?.role}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>{t('common.status')}</span>
                    <span style={{
                      fontWeight: '500',
                      color: user?.is_active ? '#10b981' : '#ef4444'
                    }}>
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '24px'
            }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>Notifications</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Email Notifications */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Email Notifications</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Receive notifications via email
                    </p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={notificationSettings.emailNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, emailNotifications: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: notificationSettings.emailNotifications ? '#3b82f6' : '#d1d5db',
                      transition: '0.4s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: notificationSettings.emailNotifications ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>

                {/* SMS Notifications */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>SMS Notifications</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Receive notifications via text message
                    </p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={notificationSettings.smsNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, smsNotifications: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: notificationSettings.smsNotifications ? '#3b82f6' : '#d1d5db',
                      transition: '0.4s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: notificationSettings.smsNotifications ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Push Notifications */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Push Notifications</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Receive push notifications in your browser
                    </p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={notificationSettings.pushNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, pushNotifications: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: notificationSettings.pushNotifications ? '#3b82f6' : '#d1d5db',
                      transition: '0.4s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: notificationSettings.pushNotifications ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Reminder Notifications */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Appointment Reminders</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Get reminders before appointments
                    </p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={notificationSettings.reminderNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, reminderNotifications: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: notificationSettings.reminderNotifications ? '#3b82f6' : '#d1d5db',
                      transition: '0.4s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: notificationSettings.reminderNotifications ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Marketing Emails */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>Marketing Emails</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Receive news, tips and special offers
                    </p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={notificationSettings.marketingEmails}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, marketingEmails: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: notificationSettings.marketingEmails ? '#3b82f6' : '#d1d5db',
                      transition: '0.4s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: notificationSettings.marketingEmails ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              <button
                onClick={saveNotificationSettings}
                disabled={isLoading}
                style={{
                  marginTop: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          )}

          {/* Push Notifications Section */}
          {activeSection === 'push-notifications' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '24px'
            }}>
              <NotificationSettings />
            </div>
          )}

          {/* Notification History Section */}
          {activeSection === 'notification-history' && (
            canUseNotificationHistory ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                padding: '24px'
              }}>
                <NotificationHistory />
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                padding: '24px',
                color: '#6b7280'
              }}>
                <p style={{ margin: 0 }}>
                  O histórico de notificações está desabilitado para esta empresa via feature flag.
                </p>
              </div>
            )
          )}

          {/* SMTP Configuration Section */}
          {activeSection === 'smtp' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '24px'
            }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>{t('smtp.settingsTitle')}</h2>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                  Servidor SMTP
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Host SMTP *
                    </label>
                    <input
                      type="text"
                      value={smtpConfig.host}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Porta *
                    </label>
                    <input
                      type="number"
                      value={smtpConfig.port}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) || 587 })}
                      min="1"
                      max="65535"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      <input
                        type="checkbox"
                        checked={smtpConfig.secure}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, secure: e.target.checked })}
                        style={{ width: '16px', height: '16px' }}
                      />
                      Usar TLS/SSL
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>{t('settings.auth')}</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>{t('common.user')}</label>
                    <input
                      type="text"
                      value={smtpConfig.username}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                      placeholder={t('login.emailPlaceholder')}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>{t('common.password')}</label>
                    <input
                      type="password"
                      value={smtpConfig.password}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                      placeholder="••••••••"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Email Remetente *
                    </label>
                    <input
                      type="email"
                      value={smtpConfig.from}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, from: e.target.value })}
                      placeholder="notificacoes@exemplo.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#0369a1' }}>
                  Testar Configuração
                </h4>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Email para teste *
                    </label>
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="teste@exemplo.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <button
                    onClick={testSMTPConfig}
                    disabled={isLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isLoading ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isLoading ? 'Enviando...' : 'Enviar Teste'}
                  </button>
                </div>
              </div>

              <button
                onClick={saveSMTPConfig}
                disabled={isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Salvando...' : 'Salvar Configuração'}
              </button>
            </div>
          )}

          {/* WhatsApp Configuration Section */}
          {activeSection === 'whatsapp' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '24px'
            }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>{t('settings.whatsapp')}</h2>

              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                  ℹ️ Informações
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534', lineHeight: 1.6 }}>
                  Configure suas credenciais do Twilio WhatsApp Business API. Você pode obter essas informações no painel do Twilio.
                  O número deve estar no formato: <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: '3px' }}>+5511999999999</code> ou <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: '3px' }}>whatsapp:+5511999999999</code>
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                  Credenciais Twilio
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Account SID *
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.account_sid}
                      onChange={(e) => setWhatsAppConfig({ ...whatsappConfig, account_sid: e.target.value })}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Auth Token *
                    </label>
                    <input
                      type="password"
                      value={whatsappConfig.auth_token}
                      onChange={(e) => setWhatsAppConfig({ ...whatsappConfig, auth_token: e.target.value })}
                      placeholder="••••••••"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Número WhatsApp (From) *
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.from_number}
                      onChange={(e) => setWhatsAppConfig({ ...whatsappConfig, from_number: e.target.value })}
                      placeholder={t('settings.phonePlaceholder')}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Formato: +5511999999999 (o prefixo whatsapp: será adicionado automaticamente)
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#0369a1' }}>
                  Testar Configuração
                </h4>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Número para teste *
                    </label>
                    <input
                      type="text"
                      value={testWhatsAppNumber}
                      onChange={(e) => setTestWhatsAppNumber(e.target.value)}
                      placeholder="+5511999999999"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <button
                    onClick={testWhatsAppConfig}
                    disabled={isLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#25D366',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isLoading ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isLoading ? 'Enviando...' : 'Enviar Teste'}
                  </button>
                </div>
              </div>

              <button
                onClick={saveWhatsAppConfig}
                disabled={isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Salvando...' : 'Salvar Configuração'}
              </button>
            </div>
          )}

          {/* Company Customization Section */}
          {activeSection === 'company-customization' && (
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                padding: '24px',
              }}
            >
              <CompanyCustomizationManager />
            </div>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              <TwoFactorSettings />

              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '24px',
                }}
              >
                <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>{t('security')}</h2>

                <form onSubmit={changePassword}>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                      {t('changePassword')}
                    </h3>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                        {t('currentPassword')} *
                      </label>
                      <input
                        type="password"
                        required
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <PasswordInput
                      label={t('newPassword')}
                      value={passwordData.newPassword}
                      onChange={(value) => setPasswordData({ ...passwordData, newPassword: value })}
                      required
                      showStrength
                    />

                    <PasswordInput
                      label={t('confirmNewPassword')}
                      value={passwordData.confirmPassword}
                      onChange={(value) => setPasswordData({ ...passwordData, confirmPassword: value })}
                      required
                      showStrength={false}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    <Shield size={16} />
                    {isLoading ? t('changing') : t('changePassword')}
                  </button>
                </form>

                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #fde68a'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    Password Requirements:
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#92400e' }}>
                    <li>Minimum 8 characters</li>
                    <li>At least one number</li>
                    <li>At least one symbol (!@#$%...)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Configuracoes;
